import { memo, useCallback } from 'react';
import { Particles } from 'react-particles';
import { useNavigate } from 'react-router-dom';
import { loadSlim } from 'tsparticles-slim';
import { ButtonPromo, StepCSSTransitionGroup } from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import { t } from '~/helpers/localization';
import { ROUTES_MAP } from '~/router/routes';
import { useAppSelector } from '~/store';
import { isClanMemberSelector, isLoggedInSelector } from '~/store/selectors/currentAccountSelector';
import { logIn } from '~/utils/auth';

import { DotList, Title, PageHeadImage } from '~/UIKit';

import styles from './ClanBattlesInfoPage.scss';
import { circle, polygon } from './images';
import imageBg from './images/clan_battles.png';
import imageBgCN from './images/clan_battles_cn360.png';

import { clanBattlesDescription } from './promoDescription';

import type { Engine, IOptions, RecursivePartial } from 'tsparticles-engine';

const particlesParams: RecursivePartial<IOptions> = {
  emitters: {
    position: {
      x: 50,
      y: 50,
    },
  },
  fullScreen: {
    enable: false,
    zIndex: 0,
  },
  fpsLimit: 120,
  detectRetina: true,
  particles: {
    number: {
      value: 12,
      density: {
        enable: true,
        value_area: 400,
      },
    },
    shape: {
      type: ['image'],
      image: [
        {
          src: circle,
          height: 20,
          width: 20,
        },
        {
          src: polygon,
          height: 30,
          width: 30,
        },
      ],
    },
    opacity: {
      value: 0.5,
      random: true,
      anim: {
        enable: false,
      },
    },
    size: {
      value: 30,
      random: true,
      anim: {
        enable: true,
        speed: 5,
        size_min: 24,
        sync: false,
      },
    },
    line_linked: {
      enable: false,
    },
    move: {
      enable: true,
      speed: 0.6,
      direction: 'none',
      random: true,
      straight: false,
      out_mode: 'bounce',
      bounce: false,
    },
  },
};

const ClanBattlesInfoPage = () => {
  const navigate = useNavigate();

  const isClanMember = useAppSelector(isClanMemberSelector);
  const isLoggedIn = useAppSelector(isLoggedInSelector);

  const navigateToSearch = () => {
    navigate(`${root}${ROUTES_MAP.SEARCH}`);
  };

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
  }, []);

  return (
    <div className={styles.wrapper}>
      <PageHeadImage
        className={styles.headImage}
        opacity={0.8}
        image={imageBg}
        imageCN={imageBgCN}
        width={1248}
        height={764}
      />
      <Particles className={styles.particles} init={particlesInit} options={particlesParams} />
      <div className={styles.inline}>
        <div className={styles.side}>
          <Title>{clanBattlesDescription.title}</Title>
          <DotList>{clanBattlesDescription.items}</DotList>
        </div>
      </div>
      {isClanMember ? null : (
        <StepCSSTransitionGroup level={4}>
          <div className={styles.inviteRow}>
            {isLoggedIn ? (
              <ButtonPromo isFlat isCenter onClick={navigateToSearch}>
                {t('Хочу в клан')}
              </ButtonPromo>
            ) : (
              <ButtonPromo isFlat isCenter onClick={logIn}>
                {t('Войти')}
              </ButtonPromo>
            )}
          </div>
        </StepCSSTransitionGroup>
      )}
    </div>
  );
};

export default memo(ClanBattlesInfoPage);
